/* Specimen index: a live ferrule stage driven by the collection design list. */
import { FINISHES, DEFAULT_FINISH, addLights, parseGeometry } from '@theme/ferrule-mesh';

const AMBIENT_SPEED = 0.15;
const TRANSITION_MS = 520;
const DRAG_RESUME_MS = 1200;
const SPIN_IN = 0.9;
const CAMERA_FOV = 28;
const CAMERA_ELEVATION = 0.2;
const MESH_TILT = 0.08;

const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const pad = (value) => String(value).padStart(2, '0');

class FerruleIndex extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const signal = this.abort.signal;

    this.catalog = JSON.parse(this.querySelector('[data-ferrule-index-catalog]')?.textContent || '[]');
    this.products = new Map(this.catalog.map((product) => [product.handle, product]));
    this.rows = [...this.querySelectorAll('[data-index-row]')].filter((row) => this.products.has(row.dataset.handle));
    this.list = this.querySelector('[data-index-list]');
    this.stageWrap = this.querySelector('.ferrule-index__stage-wrap');
    this.stage = this.querySelector('[data-stage]');
    this.canvas = this.stage.querySelector('canvas');
    this.poster = this.stage.querySelector('[data-poster]');
    this.ghost = this.querySelector('[data-ghost-index]');
    this.caption = this.querySelector('[data-caption]');
    this.counter = this.querySelector('[data-counter]');
    this.status = this.querySelector('[data-status]');
    this.finishButtons = [...this.querySelectorAll('[data-finish]')];

    this.reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverQuery = matchMedia('(hover: hover) and (pointer: fine)');
    this.mobileQuery = matchMedia('(max-width: 768px)');

    const defaultHandle = this.dataset.defaultHandle;
    this.activeHandle = this.products.has(defaultHandle) ? defaultHandle : this.rows[0]?.dataset.handle;
    this.finish = this.finishButtons[0]?.dataset.finish || DEFAULT_FINISH;
    this.finishChosen = false;

    this.angle = 0;
    this.velocity = 0;
    this.lastDragAt = -Infinity;
    this.meshes = new Map();
    this.meshLoads = new Map();
    this.failed = new Set();
    this.visible = true;
    this.hovering = false;
    this.userScrolled = false;
    this.needsRender = true;

    if (!this.activeHandle) return;

    this.bindList(signal);
    this.bindStage(signal);
    this.bindFinishes(signal);

    addEventListener('scroll', () => {
      if (this.visible) this.updateFraming();
      if (this.userScrolled) return;
      this.userScrolled = true;
      if (this.centeredHandle && !this.hovering) this.setActive(this.centeredHandle);
    }, { passive: true, signal });
    addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.observeRows(), 150);
    }, { passive: true, signal });

    this.observeRows();
    this.updateUI();
    this.boot().catch(() => this.fallback());
  }

  get reducedMotion() {
    return this.reducedMotionQuery.matches;
  }

  bindList(signal) {
    this.list.addEventListener('pointerenter', () => {
      if (this.hoverQuery.matches) this.hovering = true;
    }, { signal });
    this.list.addEventListener('pointerleave', () => { this.hovering = false; }, { signal });
    this.rows.forEach((row) => {
      const handle = row.dataset.handle;
      row.addEventListener('pointerenter', () => {
        if (this.hoverQuery.matches) this.setActive(handle);
      }, { signal });
      row.addEventListener('focus', () => this.setActive(handle), { signal });
      // Runs before navigation, so the followed href reflects the chosen finish.
      row.addEventListener('click', () => this.setActive(handle), { signal });
    });
  }

  bindStage(signal) {
    this.canvas.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.angle += event.key === 'ArrowLeft' ? -0.18 : 0.18;
      this.velocity = 0;
      this.lastDragAt = performance.now();
      this.requestRender();
    }, { signal });
    this.canvas.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      this.pointer = { id: event.pointerId, x: event.clientX, time: performance.now() };
      this.velocity = 0;
      this.canvas.setPointerCapture(event.pointerId);
    }, { signal });
    this.canvas.addEventListener('pointermove', (event) => {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const now = performance.now();
      const delta = (event.clientX - this.pointer.x) * 0.009;
      this.angle += delta;
      this.velocity = delta / Math.max(8, now - this.pointer.time);
      this.pointer.x = event.clientX;
      this.pointer.time = now;
      this.lastDragAt = now;
      this.requestRender();
    }, { signal });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((name) => {
      this.canvas.addEventListener(name, () => {
        if (!this.pointer) return;
        if (performance.now() - this.pointer.time > 80) this.velocity = 0;
        this.pointer = null;
        this.lastDragAt = performance.now();
        this.requestRender();
      }, { signal });
    });
    this.canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      this.fallback();
    }, { signal });
  }

  bindFinishes(signal) {
    this.finishButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.finish = button.dataset.finish;
        this.finishChosen = true;
        this.updateUI();
        this.meshes.forEach((mesh) => this.applyFinish(mesh));
        this.requestRender();
      }, { signal });
    });
  }

  observeRows() {
    this.rowObserver?.disconnect();
    if (!this.rows.length) return;
    const line = this.bandLine();
    const top = -Math.max(0, Math.round(line - 1));
    const bottom = -Math.max(0, Math.round(innerHeight - line - 1));
    this.rowObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.centeredHandle = entry.target.dataset.handle;
      });
      if (this.userScrolled && !this.hovering && this.centeredHandle) this.setActive(this.centeredHandle);
    }, { rootMargin: `${top}px 0px ${bottom}px 0px` });
    this.rows.forEach((row) => this.rowObserver.observe(row));
  }

  // Selection follows the row crossing the middle of the visible list area.
  bandLine() {
    if (!this.mobileQuery.matches) return innerHeight / 2;
    const stickyTop = parseFloat(getComputedStyle(this.stageWrap).top) || 0;
    const stageBottom = stickyTop + this.stageWrap.offsetHeight;
    return Math.min(innerHeight - 2, (stageBottom + innerHeight) / 2);
  }

  async boot() {
    this.T = await import(this.dataset.three);
    if (!this.isConnected) return;
    this.renderer = new this.T.WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true, powerPreference: 'low-power' });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = this.T.SRGBColorSpace;
    this.renderer.toneMapping = this.T.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = FINISHES.Black.exposure;

    this.scene = new this.T.Scene();
    this.camera = new this.T.PerspectiveCamera(CAMERA_FOV, 1, 0.1, 200);
    addLights(this.T, this.scene);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.stage);
    this.stageObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) this.requestRender();
      else this.stopLoop();
    });
    this.stageObserver.observe(this.stage);
    this.resize();

    await this.showMesh(this.activeHandle);
    if (!this.renderer) return;
    this.status.textContent = this.statusMessage();
    this.loadRemaining();
  }

  async ensureMesh(handle) {
    if (this.meshes.has(handle)) return this.meshes.get(handle);
    if (this.failed.has(handle)) throw new Error(`Model unavailable for ${handle}`);
    if (!this.meshLoads.has(handle)) {
      this.meshLoads.set(handle, this.loadMesh(handle).catch((error) => {
        this.meshLoads.delete(handle);
        this.failed.add(handle);
        throw error;
      }));
    }
    return this.meshLoads.get(handle);
  }

  async loadMesh(handle) {
    const product = this.products.get(handle);
    if (!product?.model) throw new Error(`No model mapping for ${handle}`);
    const response = await fetch(product.model, { signal: this.abort.signal });
    if (!response.ok) throw new Error(`Could not load ${handle}`);
    const buffer = await response.arrayBuffer();
    if (!this.scene) throw new Error('Stage unavailable');
    const geometry = parseGeometry(this.T, buffer);
    const material = new this.T.MeshPhysicalMaterial({ side: this.T.DoubleSide });
    const mesh = new this.T.Mesh(geometry, material);
    mesh.userData = { handle };
    mesh.visible = false;
    mesh.rotation.x = MESH_TILT;
    this.applyFinish(mesh);
    this.meshes.set(handle, mesh);
    this.scene.add(mesh);
    this.renderThumbnail(handle, mesh);
    return mesh;
  }

  async loadRemaining() {
    for (const row of this.rows) {
      const handle = row.dataset.handle;
      if (!this.isConnected || !this.renderer) return;
      if (this.meshes.has(handle) || this.failed.has(handle)) continue;
      await new Promise((resolve) => requestAnimationFrame(resolve));
      try { await this.ensureMesh(handle); }
      catch (error) { /* The row keeps its poster image. */ }
    }
    this.previewMaterial?.dispose();
    this.previewRenderer?.dispose();
    this.previewRenderer?.forceContextLoss();
    this.previewRenderer = null;
  }

  renderThumbnail(handle, mesh) {
    const row = this.rows.find((item) => item.dataset.handle === handle);
    const canvas = row?.querySelector('[data-model-preview]');
    if (!canvas) return;
    let previewMesh;
    try {
      if (!this.previewRenderer) {
        this.previewRenderer = new this.T.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
        this.previewRenderer.setPixelRatio(1);
        this.previewRenderer.setSize(canvas.width, canvas.height, false);
        this.previewRenderer.outputColorSpace = this.T.SRGBColorSpace;
        this.previewRenderer.toneMapping = this.T.ACESFilmicToneMapping;
        this.previewRenderer.toneMappingExposure = 1.35;
        this.previewScene = new this.T.Scene();
        addLights(this.T, this.previewScene);
        this.previewCamera = new this.T.OrthographicCamera(-3.1, 3.1, 3.6, -3.6, 0.1, 100);
        this.previewCamera.position.set(7, 3, 12);
        this.previewCamera.lookAt(0, 0, 0);
        this.previewMaterial = new this.T.MeshStandardMaterial({ color: 0x141614, roughness: 0.32, metalness: 0.16, side: this.T.DoubleSide });
      }
      previewMesh = new this.T.Mesh(mesh.geometry, this.previewMaterial);
      this.previewScene.add(previewMesh);
      this.previewRenderer.render(this.previewScene, this.previewCamera);
      const context = canvas.getContext('2d');
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(this.previewRenderer.domElement, 0, 0, canvas.width, canvas.height);
      row.dataset.previewReady = 'true';
    } catch (error) {
      // The poster image remains visible when a thumbnail cannot render.
    } finally {
      if (previewMesh) this.previewScene.remove(previewMesh);
    }
  }

  setActive(handle) {
    if (!this.products.has(handle) || handle === this.activeHandle) return;
    this.activeHandle = handle;
    this.updateUI(true);
    this.showMesh(handle);
  }

  async showMesh(handle) {
    if (!this.renderer) return;
    let mesh;
    try {
      mesh = await this.ensureMesh(handle);
    } catch (error) {
      if (handle !== this.activeHandle || !this.renderer) return;
      this.hideMeshes();
      this.dataset.state = 'model-fallback';
      this.status.textContent = this.statusMessage();
      return;
    }
    if (handle !== this.activeHandle || !this.renderer) return;
    this.dataset.state = 'ready';
    this.transitionTo(mesh);
    this.status.textContent = this.statusMessage();
  }

  hideMeshes() {
    this.meshes.forEach((mesh) => { mesh.visible = false; });
    this.current = null;
    this.previous = null;
    this.transitionStart = null;
    this.requestRender();
  }

  transitionTo(mesh) {
    if (this.current === mesh) return;
    if (this.previous && this.previous !== mesh) this.previous.visible = false;
    this.previous = this.current;
    this.current = mesh;
    mesh.visible = true;
    this.applyFinish(mesh);
    if (this.reducedMotion || !this.previous) {
      if (this.previous) this.previous.visible = false;
      this.previous = null;
      this.transitionStart = null;
      this.setOpacity(mesh, 1);
      mesh.scale.setScalar(1);
    } else {
      this.transitionStart = performance.now();
    }
    this.requestRender();
  }

  activeFinish(product = this.products.get(this.activeHandle)) {
    const finishes = product?.finishes || [];
    if (!finishes.length || finishes.includes(this.finish)) return this.finish;
    return this.finishButtons.map((button) => button.dataset.finish).find((value) => finishes.includes(value)) || finishes[0];
  }

  applyFinish(mesh) {
    const finish = FINISHES[this.activeFinish()] || FINISHES[DEFAULT_FINISH];
    const material = mesh.material;
    material.color.setHex(finish.color);
    material.roughness = finish.roughness;
    material.metalness = finish.metalness;
    material.clearcoat = finish.clearcoat;
    material.clearcoatRoughness = 0.35;
    material.userData.baseOpacity = finish.opacity;
    this.setOpacity(mesh, mesh.userData.fade ?? 1);
    if (this.renderer) this.renderer.toneMappingExposure = finish.exposure;
  }

  setOpacity(mesh, fade) {
    const material = mesh.material;
    mesh.userData.fade = fade;
    const value = fade * (material.userData.baseOpacity ?? 1);
    const transparent = value < 0.999;
    material.opacity = transparent ? value : 1;
    material.depthWrite = !transparent;
    if (material.transparent !== transparent) {
      material.transparent = transparent;
      material.needsUpdate = true;
    }
  }

  updateUI(changed = false) {
    const product = this.products.get(this.activeHandle);
    const row = this.rows.find((item) => item.dataset.handle === this.activeHandle);
    if (!product || !row) return;
    const number = row.dataset.index || pad(this.rows.indexOf(row) + 1);
    const finish = this.activeFinish(product);

    this.rows.forEach((item) => {
      if (item === row) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });
    this.ghost.textContent = number;
    this.counter.textContent = number;
    this.caption.textContent = `FIG. ${number} — ${product.title.toUpperCase()}`;
    this.canvas.setAttribute('aria-label', `Interactive ${product.title} ferrule model in ${finish}. Drag or use arrow keys to rotate.`);
    if (product.poster) {
      this.poster.hidden = false;
      if (this.poster.getAttribute('src') !== product.poster) this.poster.src = product.poster;
      this.poster.alt = `${product.title} ferrule`;
    } else {
      this.poster.hidden = true;
    }

    this.finishButtons.forEach((button) => {
      const value = button.dataset.finish;
      const offered = product.finishes.includes(value);
      button.disabled = !offered;
      button.setAttribute('aria-pressed', String(offered && value === finish));
      button.dataset.unavailable = String(offered && !product.variantByFinish[value]);
    });

    this.updateLinks();
    if (changed) this.animateLabels();
    if (this.renderer) this.status.textContent = this.statusMessage();
  }

  updateLinks() {
    const finish = this.activeFinish();
    this.rows.forEach((row) => {
      const product = this.products.get(row.dataset.handle);
      let href = product.url;
      const variantId = product.variantByFinish[finish];
      if (this.finishChosen && row.dataset.handle === this.activeHandle && variantId) {
        href = `${product.url}${product.url.includes('?') ? '&' : '?'}variant=${variantId}`;
      }
      if (row.getAttribute('href') !== href) row.setAttribute('href', href);
    });
  }

  statusMessage() {
    const product = this.products.get(this.activeHandle);
    if (this.dataset.state === 'fallback' || this.dataset.state === 'model-fallback') return this.dataset.fallbackMessage;
    const finish = this.activeFinish(product);
    if (this.finishChosen && product.available && product.finishes.includes(finish) && !product.variantByFinish[finish]) {
      return `${finish} is sold out for ${product.title}`;
    }
    return this.hoverQuery.matches ? this.dataset.readyMessage : this.dataset.touchMessage;
  }

  animateLabels() {
    if (this.reducedMotion || !this.ghost.animate) return;
    this.ghost.animate(
      [{ opacity: 0, transform: 'translateY(6%)' }, { opacity: 1, transform: 'none' }],
      { duration: TRANSITION_MS, easing: 'cubic-bezier(.2,.8,.2,1)' }
    );
    this.caption.animate(
      [{ opacity: 0, transform: 'translateX(-10px)' }, { opacity: 1, transform: 'none' }],
      { duration: 360, easing: 'ease-out' }
    );
    if (this.dataset.state !== 'ready' && !this.poster.hidden) {
      this.poster.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease-out' });
    }
  }

  resize() {
    if (!this.renderer) return;
    const { width, height } = this.stage.getBoundingClientRect();
    if (!width || !height) return;
    this.renderer.setSize(width, height, false);
    this.stageSize = { width, height };
    const aspect = width / height;
    const mobile = this.mobileQuery.matches;
    const tan = Math.tan((CAMERA_FOV * Math.PI) / 360);
    const widthFill = mobile ? 0.5 : 0.62;
    const heightDistance = 2.75 / (mobile ? 0.5 : 0.54) / tan;
    const distance = Math.max(heightDistance, 3.1 / widthFill / (tan * aspect));
    this.heightFill = (2.75 / distance / tan) * 1.08;
    this.camera.aspect = aspect;
    this.camera.position.set(0, distance * Math.sin(CAMERA_ELEVATION), distance * Math.cos(CAMERA_ELEVATION));
    this.camera.lookAt(0, 0, 0);
    this.framing = null;
    this.updateFraming();
    this.observeRows();
  }

  // Until the stage is fully on screen, center the model in its visible part.
  updateFraming() {
    if (!this.renderer || !this.stageSize) return;
    const { width, height } = this.stageSize;
    const rect = this.stage.getBoundingClientRect();
    const top = Math.max(rect.top, 0);
    const bottom = Math.min(rect.bottom, innerHeight);
    const visible = Math.max(0, bottom - top);
    const zoom = this.reducedMotion ? 1 : Math.min(1, Math.max(0.66, visible / height / 0.8));
    const halfModel = (height * this.heightFill * zoom) / 2;
    const headClearance = this.mobileQuery.matches ? 48 : 76;
    const center = Math.min(height * 0.5, Math.max(headClearance + halfModel, (top + bottom) / 2 - rect.top));
    const offset = Math.round(height / 2 - center);
    if (this.framing?.offset === offset && this.framing?.zoom === zoom) return;
    this.framing = { offset, zoom };
    this.camera.zoom = zoom;
    this.camera.setViewOffset(width, height, 0, offset, width, height);
    this.camera.updateProjectionMatrix();
    this.requestRender();
  }

  requestRender() {
    this.needsRender = true;
    if (this.frameId || !this.renderer || !this.visible) return;
    this.frameId = requestAnimationFrame(this.frame);
  }

  stopLoop() {
    cancelAnimationFrame(this.frameId);
    this.frameId = 0;
    this.lastTime = undefined;
  }

  frame = (now) => {
    this.frameId = 0;
    if (!this.renderer || !this.isConnected) return;
    const elapsed = Math.min(64, Math.max(0, now - (this.lastTime ?? now)));
    this.lastTime = now;
    const animating = this.step(now, elapsed);
    if (animating || this.needsRender) {
      this.renderer.render(this.scene, this.camera);
      this.needsRender = false;
    }
    if (animating && this.visible) this.frameId = requestAnimationFrame(this.frame);
    else this.lastTime = undefined;
  };

  step(now, elapsed) {
    let animating = false;
    if (!this.pointer && Math.abs(this.velocity) > 0.00002) {
      this.angle += this.velocity * elapsed;
      this.velocity *= Math.exp(-elapsed / 320);
      animating = true;
    } else if (!this.pointer) {
      this.velocity = 0;
    }
    if (!this.reducedMotion && this.current) {
      if (!this.pointer && now - this.lastDragAt > DRAG_RESUME_MS) this.angle += (AMBIENT_SPEED * elapsed) / 1000;
      animating = true;
    }

    let progress = 1;
    if (this.transitionStart != null) {
      progress = Math.min(1, (now - this.transitionStart) / TRANSITION_MS);
      animating = true;
    }
    const eased = easeOut(progress);

    if (this.current) {
      this.current.scale.setScalar(0.82 + 0.18 * eased);
      this.current.rotation.y = this.angle - SPIN_IN * (1 - eased);
      this.setOpacity(this.current, eased);
    }
    if (this.previous) {
      this.previous.scale.setScalar(1 + 0.08 * eased);
      this.previous.rotation.y = this.angle + 0.35 * eased;
      this.setOpacity(this.previous, 1 - eased);
    }
    if (progress >= 1 && this.transitionStart != null) {
      this.transitionStart = null;
      if (this.previous) {
        this.previous.visible = false;
        this.setOpacity(this.previous, 1);
        this.previous.scale.setScalar(1);
        this.previous = null;
      }
    }
    return animating;
  }

  fallback() {
    this.stopLoop();
    this.dataset.state = 'fallback';
    this.renderer?.dispose();
    this.renderer = null;
    this.scene = null;
    this.status.textContent = this.dataset.fallbackMessage;
    this.updateUI();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.abort = null;
    this.stopLoop();
    clearTimeout(this.resizeTimer);
    this.rowObserver?.disconnect();
    this.stageObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.meshes?.forEach((mesh) => {
      mesh.geometry?.dispose();
      mesh.material?.dispose();
    });
    this.meshes?.clear();
    this.meshLoads?.clear();
    this.previewMaterial?.dispose();
    this.previewRenderer?.dispose();
    this.previewRenderer = null;
    this.renderer?.dispose();
    this.renderer = null;
    this.scene = null;
  }
}

if (!customElements.get('ferrule-index')) customElements.define('ferrule-index', FerruleIndex);
